<template>
  <div class="advanced-revision-index">
    <el-form
      size="small"
      label-position="top"
      class="filter-form"
    >
      <el-row
        :gutter="20"
        type="flex"
        class="filter-form__row"
      >
        <el-col :span="8">
          <el-form-item>
            <span slot="label">{{ $t('operations.search.description') }}</span>
            <el-autocomplete
              v-model="formData.description"
              value-key="label"
              clearable
              :fetch-suggestions="
                (text, cb) => {
                  searchAnalyses({ text, cb, field: 'description' });
                }
              "
              :trigger-on-focus="false"
              @select="searchOperations"
              @change="emptySelectedAnalysis"
            >
              <template slot-scope="{ item }">
                <AutocompleteAnalysis :item="item" />
              </template>
            </el-autocomplete>
          </el-form-item>
        </el-col>

        <el-col :span="6">
          <el-form-item>
            <span slot="label">{{ $t('operations.search.code') }}</span>
            <el-autocomplete
              v-model="formData.code"
              value-key="label"
              clearable
              :fetch-suggestions="
                (text, cb) => {
                  searchAnalyses({ text, cb, field: 'code' });
                }
              "
              :trigger-on-focus="false"
              @select="searchOperations"
              @change="emptySelectedAnalysis"
            >
              <template slot-scope="{ item }">
                <AutocompleteAnalysis
                  :item="item"
                  :showIcon="false"
                />
              </template>
            </el-autocomplete>
          </el-form-item>
        </el-col>
        <el-col :span="4">
          <FormItem :label="$t('analysesSearch.accredited.label')">
            <el-switch
              v-model="formData.accreditedTest"
              :active-text="$t('analysesSearch.accredited.active')"
              :inactive-text="$t('analysesSearch.accredited.inactive')"
              :active-value="true"
              :inactive-value="false"
            >
            </el-switch>
          </FormItem>
        </el-col>
      </el-row>
    </el-form>

    <Filters
      :filters="advancedRevisionFilters"
      filter-key="advancedRevision"
      :initial-data="filtersStatus.advancedRevision"
      :loading="filtersLoading"
      @search="tableRows"
    />

    <TableWrapper
      :table-columns="advancedRevisionColumns"
      table-key="advancedRevision"
      configurableColumns
      @viewReport="openViewReport"
      @advancedRevision="openWizard"
    />

    <DownloadTestReports
      v-if="openViewReportModal"
      :sample="selectedSample"
      @dialogClose="handleViewReportClose"
    />

    <AdvancedRevisionWizard
      v-if="wizardOpen"
      :sample="selectedSample"
      :lab-sample="labSample"
      :analysis-list="analysisList"
      :temperature-trails="temperatureTrails"
      :analysis-type="
        selectedSample.sampleReportType || sampleReportTypeEnum.Default
      "
      :mode="wizardMode"
      @dialogClose="handleWizardClose"
    />
  </div>
</template>

<script>
  import { mapGetters, mapActions } from 'vuex';
  import axios from 'axios';
  import { advancedRevisionColumns, advancedRevisionFilters } from '@/config';
  import { advancedRevisionApi, analysesApi } from '@/api';
  import { SampleReportType, SAMPLE_REPORT_STATUSES } from '@/config/constants';
  import TableWrapper from '@/components/TableWrapper.vue';
  import Filters from '@/components/Filters.vue';
  import AutocompleteAnalysis from '@/components/AutocompleteAnalysis.vue';
  import FormItem from '@/components/FormItem.vue';
  import filterLoadingMixin from '@/mixins/filterLoadingMixin';
  import abortControllerMixin from '@/mixins/abortControllerMixin';
  import DownloadTestReports from '@/views/analysesStatusManagement/DownloadTestReports.vue';
  import AdvancedRevisionWizard from './AdvancedRevisionWizard.vue';

  const initialFormData = {
    description: '',
    code: '',
    analysisId: null,
    accreditedTest: null,
  };

  export default {
    name: 'AdvancedRevisionIndex',
    mixins: [filterLoadingMixin, abortControllerMixin],
    components: {
      TableWrapper,
      Filters,
      AutocompleteAnalysis,
      FormItem,
      DownloadTestReports,
      AdvancedRevisionWizard,
    },
    data() {
      return {
        advancedRevisionColumns,
        advancedRevisionFilters,
        sampleReportTypeEnum: SampleReportType,
        formData: { ...initialFormData },
        selectedSample: null,
        labSample: null,
        analysisList: [],
        temperatureTrails: [],
        wizardOpen: false,
        wizardMode: 'postSignature',
        openViewReportModal: false,
      };
    },
    computed: {
      ...mapGetters(['tableFilters', 'filtersStatus', 'tablesStatus']),
    },
    watch: {},
    methods: {
      ...mapActions([
        'updateTableRows',
        'updateTableCount',
        'parseQueryStringStatus',
      ]),
      async tableRows() {
        const signal = this.createAbortController();
        this.filtersLoading = true;
        let aborted = false;
        try {
          const filterStatus = { ...this.filtersStatus.advancedRevision };
          const extraWhere = {};
          if (this.formData.analysisId) {
            extraWhere.analysisId = this.formData.analysisId;
          }
          if (this.formData.accreditedTest !== null) {
            extraWhere.accreditedTest = this.formData.accreditedTest;
          }

          const [rowsResponse, countResponse] = await Promise.all([
            advancedRevisionApi.search(
              this.tableFilters('advancedRevision'),
              filterStatus,
              extraWhere,
              { signal }
            ),
            advancedRevisionApi.searchCount(filterStatus, extraWhere, {
              signal,
            }),
          ]);

          this.updateTableRows({
            tableKey: 'advancedRevision',
            rows: rowsResponse.data,
          });
          this.updateTableCount({
            tableKey: 'advancedRevision',
            count: countResponse.data.count,
          });
        } catch (error) {
          if (axios.isCancel(error)) {
            aborted = true;
            return;
          }
          this.$notify({
            showClose: true,
            type: 'error',
            message: error.response.data.error.message,
          });
        } finally {
          if (!aborted) this.filtersLoading = false;
        }
      },
      searchOperations(item) {
        this.formData.code = item.code;
        this.formData.description = item.description;
        this.formData.analysisId = item.value;
      },
      emptySelectedAnalysis(text) {
        if (text === '') {
          this.formData = { ...initialFormData };
        }
      },
      async searchAnalyses({ text, cb, field }) {
        if (text.length >= 3) {
          const response = await analysesApi.autocompleteFetch(
            field,
            text,
            true,
            false
          );
          const analyses = response.data.map((a) => ({
            value: a.id,
            code: a.code,
            description: a.description,
            method: a.method,
            accreditedTest: a.accreditedTest,
            instrument: a.brand,
          }));
          cb(analyses);
        } else {
          cb([]);
        }
      },
      openViewReport(row) {
        this.selectedSample = row;
        this.openViewReportModal = true;
      },
      handleViewReportClose() {
        this.openViewReportModal = false;
        this.selectedSample = null;
      },
      openWizard(row) {
        this.selectedSample = row;
        this.wizardMode =
          row.sampleReportStatus === SAMPLE_REPORT_STATUSES.APPROVED
            ? 'postSignature'
            : 'preSignature';
        this.wizardOpen = true;
      },
      handleWizardClose(status = {}) {
        this.wizardOpen = false;
        this.selectedSample = null;
        if (status.success) this.tableRows();
      },
    },
    created() {
      const query = { ...this.$route.query };
      if (!query.sorting) {
        query.sorting = JSON.stringify({
          prop: 'lastReportDate',
          order: 'descending',
        });
        query.tableKey = 'advancedRevision';
      }
      this.parseQueryStringStatus(query);
      this.tableRows();
    },
    beforeRouteUpdate(to, from, next) {
      this.parseQueryStringStatus(to.query);
      this.tableRows();
      next();
    },
  };
</script>
